import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import {
    Badge,
    Button,
    Col,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
} from "reactstrap";
import {
    getAllPaymentsAPI,
    getPaymentByIdAPI,
    updatePaymentStatusAPI,
} from "../../../../api/adminPaymentAPIs.ts";
import {
    BlockBetween,
    BlockContent,
    BlockDes,
    BlockHead,
    BlockHeadContent,
    BlockTitle,
    Icon,
    PreviewCard,
    ReactDataTable,
} from "../../../../components/Component";
import Content from "../../../../layout/content/Content";
import Head from "../../../../layout/head/Head";

const PaymentList = () => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusDropdownOpen, setStatusDropdownOpen] = useState({});
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchPayments = async () => {
            try {
                setLoading(true);
                const response = await getAllPaymentsAPI();
                setPayments(response.data.data);
            } catch (error) {
                console.error("Failed to fetch payments:", error);
                toast.error("Failed to fetch payments");
            } finally {
                setLoading(false);
            }
        };

        fetchPayments();
    }, []);

    const toggleStatusDropdown = (paymentId) => {
        setStatusDropdownOpen((prev) => ({
            ...prev,
            [paymentId]: !prev[paymentId],
        }));
    };

    const handleStatusUpdate = async (paymentId, newStatus) => {
        try {
            await updatePaymentStatusAPI({
                id: paymentId,
                body: { status: newStatus },
            });
            setPayments((prevPayments) =>
                prevPayments.map((payment) =>
                    payment._id === paymentId ? { ...payment, status: newStatus } : payment
                )
            );
            if (selectedPayment && selectedPayment._id === paymentId) {
                setSelectedPayment((prev) => ({ ...prev, status: newStatus }));
            }
            toast.success(`Payment ${paymentId} status updated to ${newStatus}`);
        } catch (error) {
            console.error("Failed to update payment status:", error);
            toast.error("Failed to update payment status");
        }
    };

    const toggleModal = () => setModalOpen((prev) => !prev);

    const handleViewPayment = async (paymentId) => {
        try {
            const response = await getPaymentByIdAPI({ id: paymentId });
            setSelectedPayment(response.data.data);
            toggleModal();
        } catch (error) {
            console.error("Failed to fetch payment details:", error);
            toast.error("Failed to fetch payment details");
        }
    };

    const statusOptions = ["Pending", "Completed", "Failed", "Refunded"];

    const columns = [
        {
            name: "Payment ID",
            selector: (row) => row._id,
            cell: (row) => (
                <span
                    onClick={() => navigate(`/admin/payment/${row._id}`)}
                    className="fw-bold text-primary cursor-pointer"
                >
                    {row._id}
                </span>
            ),
            sortable: true,
        },
        {
            name: "User",
            selector: (row) => `${row.userId.first_name} ${row.userId.last_name}`,
            cell: (row) => (
                <div>
                    <p className="mb-0 fw-medium">{`${row.userId.first_name} ${row.userId.last_name}`}</p>
                    <p className="text-muted small">{row.userId.email}</p>
                </div>
            ),
            sortable: true,
        },
        {
            name: "Order ID",
            selector: (row) => row.orderId?._id || "N/A",
            cell: (row) => <span>{row.orderId?._id || "N/A"}</span>,
            sortable: true,
        },
        {
            name: "Amount",
            selector: (row) => row.amount,
            cell: (row) => (
                <span className="text-dark fw-bold">₹{Number(row.amount).toFixed(2)}</span>
            ),
            sortable: true,
        },
        {
            name: "Method",
            selector: (row) => row.paymentMethod,
            cell: (row) => <span>{row.paymentMethod}</span>,
            sortable: true,
        },
        {
            name: "Status",
            selector: (row) => row.status,
            cell: (row) => (
                <Dropdown
                    isOpen={statusDropdownOpen[row._id] || false}
                    toggle={() => toggleStatusDropdown(row._id)}
                >
                    <DropdownToggle
                        caret
                        color={
                            row.status === "Completed"
                                ? "success"
                                : row.status === "Failed" || row.status === "Refunded"
                                    ? "danger"
                                    : "warning"
                        }
                        className="btn-sm px-3 py-1"
                    >
                        {row.status}
                    </DropdownToggle>
                    <DropdownMenu>
                        {statusOptions.map((status) => (
                            <DropdownItem
                                key={status}
                                onClick={() => handleStatusUpdate(row._id, status)}
                                disabled={row.status === status}
                            >
                                {status}
                            </DropdownItem>
                        ))}
                    </DropdownMenu>
                </Dropdown>
            ),
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex">
                    <Button
                        color="info"
                        size="sm"
                        onClick={() => handleViewPayment(row._id)}
                    >
                        <Icon name="eye" />
                    </Button>
                    <Button
                        color="primary"
                        size="sm"
                        className="ms-2"
                        onClick={() => navigate(`/admin/payment/${row._id}`)}
                    >
                        <Icon name="edit" />
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <React.Fragment>
            <Head title="Payment List" />
            <Content>
                <BlockHead size="sm">
                    <BlockBetween>
                        <BlockHeadContent>
                            <BlockTitle tag="h3" page>
                                Payment List
                            </BlockTitle>
                            <BlockDes className="text-soft">
                                <p>Total payments: {payments.length}</p>
                            </BlockDes>
                        </BlockHeadContent>
                    </BlockBetween>
                </BlockHead>
                <BlockContent>
                    <PreviewCard>
                        {loading ? (
                            <div
                                className="d-flex justify-content-center align-items-center"
                                style={{ height: "50vh" }}
                            >
                                <div className="text-center">
                                    <div
                                        className="spinner-grow text-primary"
                                        style={{ width: "3rem", height: "3rem" }}
                                        role="status"
                                    >
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3 text-muted">Loading payments, please wait...</p>
                                </div>
                            </div>
                        ) : (
                            <ReactDataTable
                                data={payments}
                                columns={columns}
                                pagination
                                actions
                                expandableRows
                            />
                        )}
                    </PreviewCard>
                </BlockContent>

                {/* View Payment Modal */}
                <Modal isOpen={modalOpen} toggle={toggleModal} size="lg">
                    <ModalHeader toggle={toggleModal} className="bg-primary text-white">
                        Payment Details - {selectedPayment?._id}
                    </ModalHeader>
                    <ModalBody>
                        {selectedPayment ? (
                            <Row>
                                <Col md={6}>
                                    <div className="mb-4">
                                        <h6 className="text-muted fw-medium">User</h6>
                                        <p className="fw-bold mb-1">{`${selectedPayment.userId.first_name} ${selectedPayment.userId.last_name}`}</p>
                                        <p className="text-muted">{selectedPayment.userId.email}</p>
                                    </div>
                                    <div className="mb-4">
                                        <h6 className="text-muted fw-medium">Order Details</h6>
                                        <p className="mb-1">Order ID: {selectedPayment.orderId?._id || "N/A"}</p>
                                        <p className="mb-1">
                                            Order Date:{" "}
                                            {selectedPayment.orderId?.orderDate
                                                ? new Date(selectedPayment.orderId.orderDate).toLocaleString()
                                                : "N/A"}
                                        </p>
                                        <p className="text-dark fw-bold">
                                            Total: ₹
                                            {selectedPayment.orderId?.totalAmount?.toFixed(2) || "N/A"}
                                        </p>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-4">
                                        <h6 className="text-muted fw-medium">Payment Details</h6>
                                        <p className="mb-1">Method: {selectedPayment.paymentMethod}</p>
                                        <p className="mb-1">
                                            Transaction ID: {selectedPayment.transactionId}
                                        </p>
                                        <p className="text-dark fw-bold">
                                            Amount: ₹{Number(selectedPayment.amount).toFixed(2)}
                                        </p>
                                    </div>
                                    <div className="mb-4">
                                        <h6 className="text-muted fw-medium">Status</h6>
                                        <Badge
                                            color={
                                                selectedPayment.status === "Completed"
                                                    ? "success"
                                                    : selectedPayment.status === "Failed" ||
                                                        selectedPayment.status === "Refunded"
                                                        ? "danger"
                                                        : "warning"
                                            }
                                            className="px-3 py-2"
                                        >
                                            {selectedPayment.status}
                                        </Badge>
                                    </div>
                                    <div className="mb-4">
                                        <h6 className="text-muted fw-medium">Timeline</h6>
                                        <p className="mb-1">
                                            Created: {new Date(selectedPayment.createdAt).toLocaleString()}
                                        </p>
                                        <p className="mb-1">
                                            Updated: {new Date(selectedPayment.updatedAt).toLocaleString()}
                                        </p>
                                    </div>
                                    {selectedPayment.refundDetails?.refundId && (
                                        <div className="mb-4">
                                            <h6 className="text-muted fw-medium">Refund Details</h6>
                                            <p className="mb-1">
                                                Refund ID: {selectedPayment.refundDetails.refundId || "N/A"}
                                            </p>
                                            <p className="mb-1">
                                                Amount: ₹{selectedPayment.refundDetails.amount || "N/A"}
                                            </p>
                                            <p className="mb-1">
                                                Reason: {selectedPayment.refundDetails.reason || "N/A"}
                                            </p>
                                            <p className="mb-1">
                                                Refunded At:{" "}
                                                {selectedPayment.refundDetails.refundedAt
                                                    ? new Date(
                                                        selectedPayment.refundDetails.refundedAt
                                                    ).toLocaleString()
                                                    : "N/A"}
                                            </p>
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        ) : (
                            <p>Loading payment details...</p>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={toggleModal}>
                            Close
                        </Button>
                        <Button
                            color="primary"
                            onClick={() => {
                                toggleModal();
                                navigate(`/admin/payment/${selectedPayment?._id}`);
                            }}
                        >
                            Edit Payment
                        </Button>
                    </ModalFooter>
                </Modal>
            </Content>
        </React.Fragment>
    );
};

export default PaymentList;